/**
 * Record history, browser side: what the entries mean and how they are shown.
 *
 * Entries are written by the record-history-capture function into `history`,
 * in the flickquote-system database, a few minutes after a record stops
 * changing, and expire HISTORY_RETENTION_DAYS later. Each one is readable by
 * exactly the people who can read the record. This module is pure — the reads
 * are in services/history.js — so everything here is unit tested
 * (lib/history.test.js).
 *
 * An entry's `changes` is JSON, a list of:
 *   { field, before, after }                  a value changed
 *   { field, at, removed, added }             a long text edit (only the window)
 *   { field, list: true, added, removed, changed }   items in a list column
 *   { field, list: true, beforeCount, afterCount }   a list without item ids
 *   { baseline: true }                        history starts here
 *   { truncated: true, fields }               too big to keep; which fields
 */

/** How long history is kept. Matches HISTORY_RETENTION_DAYS in record-registry.js (pinned by test). */
export const HISTORY_RETENTION_DAYS = 90;

export const HISTORY_DATABASE_ID = 'flickquote-system';
export const HISTORY_TABLE = 'history';

/** Consecutive entries by the same person on the same record this close together are shown as one. */
export const GROUP_WINDOW_MS = 10 * 60 * 1000;

/**
 * What each captured table is called. Exactly the tables history captures,
 * pinned to appwrite.config.json by lib/history.test.js.
 */
export const TABLE_LABELS = {
  quote_requests: 'Quote request',
  quote_request_images: 'Photo',
  notes: 'Note',
  quotes: 'Quote',
  vehicle_damage: 'Damage',
  customers: 'Customer',
  customer_vehicles: 'Vehicle',
  customer_organisations: 'Organisation',
  customer_organisation_links: 'Organisation link',
  customer_relationships: 'Relationship',
  fleet_vehicles: 'Fleet vehicle',
  fleet_bookings: 'Booking',
  fleet_vehicle_events: 'Fleet event',
  org_line_allowances: 'Shop line',
  'panel-quote-insurer-map': 'Insurer mapping',
};

/** The record-type filter in Settings → History. */
export const TABLE_GROUPS = [
  { id: 'quote-requests', label: 'Quote requests', tables: ['quote_requests', 'quote_request_images', 'notes', 'quotes', 'vehicle_damage'] },
  { id: 'customers', label: 'Customers & vehicles', tables: ['customers', 'customer_vehicles', 'customer_relationships'] },
  { id: 'organisations', label: 'Organisations', tables: ['customer_organisations', 'customer_organisation_links'] },
  { id: 'fleet', label: 'Fleet', tables: ['fleet_vehicles', 'fleet_bookings', 'fleet_vehicle_events'] },
  { id: 'settings', label: 'Settings', tables: ['org_line_allowances', 'panel-quote-insurer-map'] },
];

export const ACTION_LABELS = {
  create: 'Created',
  update: 'Changed',
  delete: 'Sent to the recycle bin',
  restore: 'Restored',
  remove: 'Removed',
};

/** Field names that do not read well turned from camelCase. */
const FIELD_LABELS = {
  rego: 'Registration',
  vin: 'VIN',
  gst: 'GST',
  damageItems: 'Damage',
  lines: 'Quote lines',
  customerId: 'Customer',
  customerVehicleId: 'Vehicle',
  customerOrganisationId: 'Organisation',
  relatedRecordId: 'Record',
  quoteRequestId: 'Quote request',
  panelQuoteJobId: 'Panel Quote job',
  panelQuoteJobNumber: 'Panel Quote job number',
  insurerId: 'Insurer',
  standardLineId: 'Standard line',
  vehicle: 'Vehicle',
  quoteRequest: 'Quote request',
  imageCategory: 'Photo type',
  imageName: 'Name',
};

/** "firstName" → "First name", "panel_quote_id" → "Panel quote id". */
export function fieldLabel(field) {
  if (!field) return '';
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  const words = String(field)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})?)?$/;

/** A recorded value as a person reads it. */
export function formatValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (Array.isArray(value)) return value.length ? value.map(formatValue).join(', ') : '—';
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'string' && ISO_DATE.test(value)) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      const dateOnly = value.length === 10 || /T00:00:00(\.000)?(Z|\+00:00)$/.test(value);
      return date.toLocaleString('en-NZ', dateOnly
        ? { day: 'numeric', month: 'short', year: 'numeric' }
        : { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    }
  }
  return String(value);
}

/** The changes an entry holds, parsed; [] for none or for JSON that will not parse. */
export function parseChanges(entry) {
  if (!entry?.changes) return [];
  try {
    const parsed = JSON.parse(entry.changes);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function isBaseline(entry) {
  return parseChanges(entry).some((c) => c && c.baseline);
}

/** Who made the change, as shown: their name, or System for a function. */
export function actorOf(entry) {
  return entry?.actorName || (entry?.actorUserId ? 'Someone' : 'System');
}

const timeOf = (entry) => Date.parse(entry?.rowUpdatedAt || entry?.$createdAt || 0) || 0;

const isScalar = (change) => change && change.field && 'before' in change && 'after' in change && !change.list;

const sameValue = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Fold the changes of a later entry into an earlier group's. Both lists are in
 * the order they happened: `older` first.
 *
 * A value keeps its earliest `before` and its latest `after`, and drops out if
 * it ended where it started. Text and list edits are kept as they are, in order.
 */
function mergeChanges(older, newer) {
  const out = [];
  const scalarAt = new Map();
  for (const change of [...older, ...newer]) {
    if (isScalar(change)) {
      if (scalarAt.has(change.field)) {
        const i = scalarAt.get(change.field);
        out[i] = { ...out[i], after: change.after };
      } else {
        scalarAt.set(change.field, out.length);
        out.push({ ...change });
      }
    } else {
      out.push(change);
    }
  }
  return out.filter((c) => !(isScalar(c) && sameValue(c.before, c.after)));
}

const MERGEABLE = new Set(['create', 'update']);

/**
 * Entries, newest first, grouped for display.
 *
 * Consecutive entries by the same person on the same record within
 * GROUP_WINDOW_MS of each other become one group: a run of edits reads as one
 * line. A `create` absorbs the edits made just after it. A delete, restore or
 * remove is always its own line, and so is "history starts here".
 *
 * Returns groups, newest first:
 *   { key, tableName, rowId, label, parentRowId, action, actorName, actorUserId,
 *     at, from, changes, baseline, entries }
 */
export function groupHistory(entries) {
  const sorted = [...(entries || [])].sort((a, b) => timeOf(b) - timeOf(a));
  const groups = [];

  for (const entry of sorted) {
    const baseline = isBaseline(entry);
    const changes = baseline ? [] : parseChanges(entry);
    const previous = groups[groups.length - 1];

    // `previous` is newer than `entry`: it is the group this older entry may join.
    const joins = previous
      && !baseline && !previous.baseline
      && MERGEABLE.has(entry.action) && previous.entries.every((e) => MERGEABLE.has(e.action))
      && previous.action !== 'create' // a create is the start of a record; nothing older joins it
      && previous.tableName === entry.tableName
      && previous.rowId === entry.rowId
      && (previous.actorUserId || null) === (entry.actorUserId || null)
      && (previous.actorName || null) === (entry.actorName || null)
      && previous.from - timeOf(entry) <= GROUP_WINDOW_MS;

    if (joins) {
      previous.changes = mergeChanges(changes, previous.changes);
      previous.from = timeOf(entry);
      previous.entries.push(entry);
      if (entry.action === 'create') previous.action = 'create';
      continue;
    }

    groups.push({
      key: entry.$id,
      tableName: entry.tableName,
      rowId: entry.rowId,
      parentRowId: entry.parentRowId || null,
      label: entry.label || TABLE_LABELS[entry.tableName] || 'Record',
      action: entry.action,
      actorName: entry.actorName || null,
      actorUserId: entry.actorUserId || null,
      at: timeOf(entry),
      from: timeOf(entry),
      changes,
      baseline,
      entries: [entry],
    });
  }

  // A group of edits that came to nothing (a value changed and changed back).
  return groups.filter((g) => g.baseline || g.action !== 'update' || g.changes.length > 0);
}

/**
 * One change as lines a person reads: `{ field, text }`, where `text` is what
 * happened to it. A list edit gives one line per kind of edit.
 */
export function describeChange(change) {
  if (!change) return [];
  if (change.baseline) return [{ field: '', text: 'History starts here — earlier changes were not recorded.' }];
  if (change.truncated) {
    const fields = (change.fields || []).map(fieldLabel).join(', ');
    return [{ field: '', text: `Too large to show in full${fields ? `: ${fields}` : ''}.` }];
  }

  const field = fieldLabel(change.field);

  if (change.list) {
    if ('beforeCount' in change) {
      return [{ field, text: `${change.beforeCount} → ${change.afterCount} items` }];
    }
    const lines = [];
    if (change.added?.length) lines.push({ field, text: `added ${change.added.join(', ')}` });
    if (change.removed?.length) lines.push({ field, text: `removed ${change.removed.join(', ')}` });
    for (const item of change.changed || []) {
      const what = (item.fields || [])
        .map((f) => `${fieldLabel(f.field).toLowerCase()} ${formatValue(f.before)} → ${formatValue(f.after)}`)
        .join('; ');
      lines.push({ field, text: `${item.item}: ${what}` });
    }
    if (change.truncated) lines.push({ field, text: 'and more' });
    return lines;
  }

  if ('at' in change) {
    if (change.removed && change.added) return [{ field, text: `replaced “${change.removed}” with “${change.added}”` }];
    if (change.added) return [{ field, text: `added “${change.added}”` }];
    if (change.removed) return [{ field, text: `removed “${change.removed}”` }];
    return [{ field, text: 'edited' }];
  }

  return [{ field, text: `${formatValue(change.before)} → ${formatValue(change.after)}`, before: change.before, after: change.after }];
}

/** What a created record was given: only the fields that were set. */
export function describeCreated(changes) {
  return (changes || [])
    .filter((c) => !isScalar(c) || (c.after !== null && c.after !== undefined && c.after !== ''))
    .flatMap((c) => (isScalar(c) ? [{ field: fieldLabel(c.field), text: formatValue(c.after) }] : describeChange(c)));
}

/** "3 minutes ago", "yesterday at 4:05 pm", or a date. */
export function formatWhen(ms, now = Date.now()) {
  const date = new Date(ms);
  const diff = now - ms;
  if (diff < 60 * 1000) return 'just now';
  if (diff < 60 * 60 * 1000) {
    const minutes = Math.round(diff / 60000);
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }
  const time = date.toLocaleTimeString('en-NZ', { hour: 'numeric', minute: '2-digit' });
  const today = new Date(now);
  const yesterday = new Date(now - 24 * 60 * 60 * 1000);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(date, today)) return `today at ${time}`;
  if (sameDay(date, yesterday)) return `yesterday at ${time}`;
  return `${date.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })} at ${time}`;
}

/**
 * Merge two newest-first pages of entries — a record's own and its children's
 * — into one, keeping only what is certainly in order.
 *
 * Each page is cut at `limit`. Past the last entry of a full page there may be
 * older entries not fetched yet, so nothing older than that boundary is shown
 * until the next page; `before` is where the next page starts (inclusive — the
 * caller drops the ids it already has). `done` once both are exhausted.
 */
export function mergePages(pages, limit, seen = new Set()) {
  const full = pages.filter((p) => p.length >= limit);
  const boundary = full.length ? Math.max(...full.map((p) => timeOf(p[p.length - 1]))) : null;
  const merged = new Map();
  for (const entry of pages.flat()) {
    if (seen.has(entry.$id)) continue;
    if (boundary !== null && timeOf(entry) < boundary) continue;
    merged.set(entry.$id, entry);
  }
  const entries = [...merged.values()].sort((a, b) => timeOf(b) - timeOf(a));
  return {
    entries,
    before: boundary !== null ? new Date(boundary).toISOString() : null,
    done: boundary === null,
  };
}

const text = (value) => String(value == null ? '' : value).trim();
const clip = (value, length = 60) => {
  const t = text(value).replace(/\s+/g, ' ');
  return t.length > length ? `${t.slice(0, length - 1)}…` : t;
};

/**
 * What a record is called, from the row alone — the same names
 * record-history-capture gives its entries (record-registry.js `label`).
 */
const ROW_LABELS = {
  quote_requests: (row) => (text(row.panelQuoteJobNumber) ? `Job ${text(row.panelQuoteJobNumber)}` : 'Quote request'),
  quote_request_images: (row) => `Photo ${clip(row.imageName || row.filename, 40)}`.trim(),
  notes: (row) => `Note: ${clip(row.content, 50)}`,
  customer_organisation_links: (row) => (text(row.role) ? `Organisation link (${clip(row.role, 30)})` : 'Organisation link'),
  fleet_vehicles: (row) => text(row.registration) || clip(row.displayName) || 'Fleet vehicle',
  fleet_bookings: (row) => clip(row.title) || 'Booking',
  org_line_allowances: (row) => clip(row.lineName) || 'Shop line',
  'panel-quote-insurer-map': (row) => clip(row.insurerName) || 'Insurer mapping',
};

export function recordLabel(table, row) {
  const label = ROW_LABELS[table];
  return (label && row && label(row)) || TABLE_LABELS[table] || 'Record';
}

/** Where a record can be opened, or null when it only lives inside another view. */
export function recordLink(table, rowId, parentTable = null, parentRowId = null) {
  if (table === 'quote_requests') return `/quote-requests/${rowId}`;
  if (parentTable === 'quote_requests' && parentRowId) return `/quote-requests/${parentRowId}`;
  return null;
}
